import { useState, useRef, useCallback } from "react";
import { PanelLeftClose } from "@repo/ui";
import { Title } from "~/components/ui/title";
import type { Item, Task } from "~/types/api";
import { ItemLogView } from "./log-view/item-log-view";
import { ItemMetaView } from "./meta-view/item-meta-view";
import { ItemExplorerToolbar } from "./toolbar/item-explorer-toolbar";
import type { StatusGroup, TabWindow } from "../../task.types";

type Props = {
  selectedItems: Item[];
  tasks?: Task[];
  isLoading?: boolean;
  onClose: () => void;
  window: TabWindow<StatusGroup>;
};

export type FieldViewType = "log" | "meta";

const MIN_WIDTH = 320;
const MAX_WIDTH = 1200;
const DEFAULT_WIDTH = 672;

export const ItemDetailView = ({
  selectedItems,
  isLoading = false,
  onClose,
  window: tabWindow,
  tasks,
}: Props) => {
  const [activeView, setActiveView] = useState<FieldViewType>("log");
  const [width, setWidth] = useState<number>(DEFAULT_WIDTH);
  const isDragging = useRef(false);
  const startX = useRef(0);
  const startWidth = useRef(0);

  const handleViewChange = (view: FieldViewType) => {
    setActiveView(view);
  };

  const handleMouseMove = useCallback((e: MouseEvent) => {
    if (!isDragging.current) return;
    const delta = e.clientX - startX.current;
    const newWidth = Math.min(MAX_WIDTH, Math.max(MIN_WIDTH, startWidth.current + delta));
    setWidth(newWidth);
  }, []);

  const handleMouseUp = useCallback(() => {
    isDragging.current = false;
    document.body.style.cursor = "";
    document.body.style.userSelect = "";
    window.removeEventListener("mousemove", handleMouseMove);
    window.removeEventListener("mouseup", handleMouseUp);
  }, [handleMouseMove]);

  const handleResizeMouseDown = useCallback(
    (e: React.MouseEvent) => {
      e.preventDefault();
      isDragging.current = true;
      startX.current = e.clientX;
      startWidth.current = width;
      document.body.style.cursor = "col-resize";
      document.body.style.userSelect = "none";
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    },
    [width, handleMouseMove, handleMouseUp]
  );

  const count = selectedItems?.length ?? 0;
  const labelTitle = count === 1 ? selectedItems?.[0]?.name : `${count} selected`;

  return (
    <div
      className="border-gxe-border relative flex h-full flex-col border-r border-l bg-white"
      style={{ width }}
    >
      <ItemExplorerToolbar
        onClose={onClose}
        activeView={activeView}
        onViewChange={handleViewChange}
      />

      {count === 0 ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-2 text-gray-400">
          <PanelLeftClose className="h-8 w-8 opacity-30" />
          <p className="text-sm">Select an item to view details</p>
        </div>
      ) : (
        <>
          <Title
            headerClassName="border-b border-gray-200 overflow-hidden text-ellipsis whitespace-nowrap"
            text={`${activeView === "log" ? "Log" : "Metadata"}: ${labelTitle}`}
          />
          {activeView === "log" ? (
            <ItemLogView items={selectedItems} isLoading={isLoading} />
          ) : (
            <ItemMetaView
              items={selectedItems}
              tasks={tasks}
              isLoading={isLoading}
              window={tabWindow}
            />
          )}
        </>
      )}

      <div
        onMouseDown={handleResizeMouseDown}
        className="absolute top-0 right-0 h-full w-1 cursor-col-resize transition-colors hover:bg-blue-400 active:bg-blue-500"
        title="Drag to resize"
      />
    </div>
  );
};
